<script>
  import {appComputed, authComputed} from '@state/helpers'
  import _ from 'lodash'
  import CONSTANTS from '@src/constants'
  import moment from 'moment'
  import numeral from 'numeral'
  import Helper from '@src/plugins/vue-gridform/shared/helper'

  export default {
      name: 'UserSettingsDialog',
      props: {
        value: {
          type: Boolean,
          default: false
        }
      },
      data() {
        return {
          dialog: false,
          activeItem: ['gui'],
          activeId: 'gui',
          lang: 'de',
          locales: [],
          themes: [],
          usertheme: 'dark',
          dark: false,
          formattypes: [],
          formattype: 'iso',
          denses: [],
          denseMode: 'false',
          copyTreeItems: [],
          copyTreeItemsSelection: [],
          emailcc: '',
          emailbcc: ''
        }
      },

      computed: {
        ...authComputed,
        ...appComputed,
        items() {
          return [{
            id: 'gui',
            label: this.i18n('tree.gui'),
            icon: 'mdi-television-guide',
            children: [{
              id: 'language',
              label: this.i18n('tree.language'),
              icon: 'mdi-translate'
            }, {
              id: 'theme',
              label: this.i18n('tree.theme'),
              icon: 'mdi-format-paint'
            }, {
              id: 'dense',
              label: this.i18n('tree.dense'),
              icon: 'mdi-resize'
            }, {
              id: 'dateformat',
              label: this.i18n('tree.dateformat'),
              icon: 'mdi-calendar-account-outline'
            }]
          }, {
            id: 'mailing',
            label: this.i18n('tree.mailing'),
            icon: 'mdi-email-outline',
            children: [{
              id: 'copies',
              label: this.i18n('tree.copies'),
              icon: 'mdi-at'
            }]
          }]
        },
        organizations() {
          return this.$store.getters['api/organisations']
        },
        orgItems() {
          return this.organizations.map(org => {
            return {
              id: org.organisationId,
              name: org.name
            }
          })
        },
        bindingsTextCC() {
          return {
            label: this.$vuetify.lang.t('$vuetify.options.cc'),
            hint: this.$vuetify.lang.t('$vuetify.options.emailHint'),
            persistentHint: true,
            spellcheck: false,
            fullWidth: true,
            outlined: true,
            clearable: true,
            dense: this.dense,
            rules: [
                v => !v || Helper.isValidEmail(v) || this.$vuetify.lang.t('$vuetify.messages.emailInvalid')
            ]
          }
        },
        bindingsTextBCC() {
          return {
            label: this.$vuetify.lang.t('$vuetify.options.bcc'),
            hint: this.$vuetify.lang.t('$vuetify.options.emailHint'),
            persistentHint: true,
            spellcheck: false,
            fullWidth: true,
            outlined: true,
            clearable: true,
            dense: this.dense,
            rules: [
              v => !v || Helper.isValidEmail(v) || this.$vuetify.lang.t('$vuetify.messages.emailInvalid')
            ]
          }
        },
        emCC() {
          const {ccEmailDictionary} = this.currentUser.settings
          let cc = []
          this.copyTreeItemsSelection.forEach(copy => {
            if (!cc.length) cc = cc.concat(ccEmailDictionary[copy])
            else cc = _.intersection(cc, ccEmailDictionary[copy])
          })
          cc = cc.map(text => {
            return {text, icon: 'mdi-email-outline'}
          })
          return cc
        },
        emBCC() {
          const {bccEmailDictionary} = this.currentUser.settings
          let bcc = []
          this.copyTreeItemsSelection.forEach(copy => {
            if (!bcc.length) {
              if (bccEmailDictionary[copy]) bcc = bcc.concat(bccEmailDictionary[copy])
            }
            else bcc = _.intersection(bcc, bccEmailDictionary[copy])
          })
          bcc = bcc.map(text => {
            return {text, icon: 'mdi-email-outline'}
          })
          return bcc
        }
      },
      watch: {
        value() {
          this.dialog = this.value
        },
        dialog() {
          if (!this.dialog) this.close()
        },
        lang(lang) {
          this.$vuetify.lang.current = lang
          this.$store.dispatch('app/language', lang)
          if (this.currentUser) {
            const language = lang === 'de' ? 'German' : 'English'
            if (language !== this.currentUser.settings.language) {
              const settings = {userId: this.currentUser.user.userId, language}
              this.$store.dispatch('api/USERS_SETTINGS', settings).then(() => {
                this.updateSettings()
              })
            }
          }
        },
        usertheme(usertheme) {
          const dark = usertheme === 'dark'
          this.$vuetify.theme.dark = dark
          const theme = dark ? 'Dark' : 'Light'
          if (this.currentUser && theme !== _.get(this.currentUser, 'settings.theme')) {
            const settings = {userId: this.currentUser.user.userId, theme}
            this.$store.dispatch('api/USERS_SETTINGS', settings)
          }
          // NOTE SET_APP_THEME is needed, if user log out
          this.$store.commit('app/SET_APP_THEME', dark ? 'dark' : 'light')
        },
        denseMode(denseMode) {
          this.dense = denseMode === 'true'
          // NOTE do not save denseMode, if mobile view set denseMode
          if (this.currentUser && this.dense !== _.get(this.currentUser, 'settings.denseMode')) {
            const settings = {userId: this.currentUser.user.userId, denseMode: this.dense}
            this.$store.dispatch('api/USERS_SETTINGS', settings)
          }
        },
        organizations() {
          this.updateCopyTreeItems()
        },
        formattype() {
          if (this.formattype === this.currentUser.settings.dateFormat) return // no changes
          const dateFormat = this.formattype
          const settings = {userId: this.currentUser.user.userId, dateFormat}
          this.$store.dispatch('api/USERS_SETTINGS', settings)
          this.$store.dispatch('app/formatter', dateFormat)
        },
        copyTreeItemsSelection(selected) {
          if (_.isEmpty(selected)) this.copyTreeItemsSelection = ['global']
        }
      },

      mounted() {
        this.updateSettings()
        this.updateCopyTreeItems()
        this.dialog = this.value
      },

      methods: {
        // update app ui settings
        updateSettings() {
          const userSettings = _.get(this.currentUser, 'settings')

          const locales = Object.keys(this.$vuetify.lang.locales).map(lk => {
            return {
              value: lk,
              label: this.$vuetify.lang.t(`$vuetify.${lk}`)
            }
          })
          this.locales = _.sortBy(locales, l => l.value)

          // theme (dark or light)
          const theme = _.get(userSettings, 'theme', CONSTANTS.DEFAULT_SETTINGS.theme)
          this.dark = theme.toLowerCase() === 'dark'
          this.usertheme = theme.toLowerCase()
          this.themes = ['dark', 'light'].map(value => {
            return {
              value,
              label: this.$vuetify.lang.t(`$vuetify.themes.${value}`)
            }
          })

          // language (de or en)
          const language = _.get(userSettings, 'language', CONSTANTS.DEFAULT_SETTINGS.language)
          this.lang = language === 'German' ? 'de' : 'en'
          this.$vuetify.lang.current = this.lang
          moment.locale(this.lang)
          numeral.locale(this.lang === 'en' ? 'en-gb' : 'de')

          // date format types
          this.formattypes = ['iso', 'de', 'en'].map(value => {
            return {
              value,
              label: this.$vuetify.lang.t(`$vuetify.formattypes.${value}`)
            }
          })
          this.formattype = _.get(userSettings, 'dateFormat') || CONSTANTS.DEFAULT_SETTINGS.dateFormat

          // dense view
          this.denses = ['false', 'true'].map(value => {
            return {
              value,
              label: this.$vuetify.lang.t(`$vuetify.denses.${value}`)
            }
          })
          this.dense = _.get(userSettings, 'denseMode', CONSTANTS.DEFAULT_SETTINGS.dense)
          this.denseMode = `${this.dense}`
        },
        updateCopyTreeItems() {
          const items = []
          items.push({
            id: 'global',
            name: 'Global',
            icon: 'mdi-email-plus-outline'
          })
          items.push({
            id: 'orgs',
            name: 'Je Organisation',
            icon: 'mdi-office-building',
            children: this.orgItems
          })
          this.copyTreeItems = items
          this.copyTreeItemsSelection = ['global']
        },
        onTreeviewSelected(node) {
          this.activeId = node[0]
        },

        addEmailCC(text) {
          if (!text) return

          const ccEmailDictionary = _.clone(this.currentUser.settings.ccEmailDictionary)
          const emails = text.split(',')
          this.copyTreeItemsSelection.forEach(copy => {
            ccEmailDictionary[copy] = ccEmailDictionary[copy] || []
            ccEmailDictionary[copy] = _.uniq(ccEmailDictionary[copy].concat(emails))
          })
          const settings = {userId: this.currentUser.user.userId, ccEmailDictionary}
          this.$store.dispatch('api/USERS_SETTINGS', settings)
          this.emailcc = ''
        },
        deleteEmailCC(text) {
          const ccEmailDictionary = _.clone(this.currentUser.settings.ccEmailDictionary)
          this.copyTreeItemsSelection.forEach(copy => {
            ccEmailDictionary[copy] = ccEmailDictionary[copy] || []
            ccEmailDictionary[copy] = ccEmailDictionary[copy].filter(email => email !== text)
          })
          const settings = {userId: this.currentUser.user.userId, ccEmailDictionary}
          this.$store.dispatch('api/USERS_SETTINGS', settings)
        },
        addEmailBCC(text) {
          if (!text) return

          const bccEmailDictionary = _.clone(this.currentUser.settings.bccEmailDictionary)
          const emails = text.split(',')
          this.copyTreeItemsSelection.forEach(copy => {
            bccEmailDictionary[copy] = bccEmailDictionary[copy] || []
            bccEmailDictionary[copy] = _.uniq(bccEmailDictionary[copy].concat(emails))
          })
          const settings = {userId: this.currentUser.user.userId, bccEmailDictionary}
          this.$store.dispatch('api/USERS_SETTINGS', settings)
          this.emailbcc = ''
        },
        deleteEmailBCC(text) {
          const bccEmailDictionary = _.clone(this.currentUser.settings.bccEmailDictionary)
          this.copyTreeItemsSelection.forEach(copy => {
            bccEmailDictionary[copy] = bccEmailDictionary[copy] || []
            bccEmailDictionary[copy] = bccEmailDictionary[copy].filter(email => email !== text)
          })
          const settings = {userId: this.currentUser.user.userId, bccEmailDictionary}
          this.$store.dispatch('api/USERS_SETTINGS', settings)
        },

        close() {
          this.$emit('cancel')
        },
        i18n(key) {
          return this.$vuetify.lang.t(`$vuetify.options.${key}`)
        }
      }
    }
</script>

<template>
  <VDialog v-model="dialog" transition="scale-transition" fullscreen>
    <VSheet class="vx-usersettings__main">
      <VToolbar flat color="primary" dark :dense="dense">
        <VToolbarTitle>{{ i18n('title') }}</VToolbarTitle>
        <VSpacer/>
        <VBtn icon @click.left="close">
          <VIcon>mdi-close</VIcon>
        </VBtn>
      </VToolbar>
      <VLayout class="vx-usersettings__layout">
        <VFlex class="flex-row">
          <VSheet width="256px">
            <VTreeview
                :active="activeItem"
                :items="items"
                item-text="label"
                :dense="dense"
                activatable
                selection-type="leaf"
                open-all
                shaped
                @update:active="onTreeviewSelected"
            >
              <template v-slot:prepend="{ item }">
                <VIcon v-if="item && item.icon" small>{{ item.icon }}</VIcon>
              </template>
            </VTreeview>
          </VSheet>
          <VSheet class="vx-usersettings__content ma-4 pa-2">
            <template v-if="activeId === 'gui' || activeId === 'language'">
              <VSubheader>{{ $vuetify.lang.t('$vuetify.locales.selection') }}</VSubheader>
              <VDivider/>
              <VSheet class="px-4 mb-4">
                <VRadioGroup v-model="lang" hide-details>
                  <VRadio v-for="n in locales" :key="n.value" :label="`${n.label}`" :value="n.value"/>
                </VRadioGroup>
              </VSheet>
            </template>
            <template v-if="activeId === 'gui' || activeId === 'theme'">
              <VSubheader>{{ $vuetify.lang.t('$vuetify.options.themes') }}</VSubheader>
              <VDivider/>
              <VSheet class="px-4 mb-4">
                <VRadioGroup v-model="usertheme" hide-details>
                  <VRadio v-for="n in themes" :key="n.value" :label="`${n.label}`" :value="n.value"/>
                </VRadioGroup>
              </VSheet>
            </template>
            <template v-if="activeId === 'gui' || activeId === 'dense'">
              <VSubheader>{{ $vuetify.lang.t('$vuetify.options.denses') }}</VSubheader>
              <VDivider/>
              <VSheet class="px-4 mb-4">
                <VRadioGroup v-model="denseMode" hide-details>
                  <VRadio v-for="n in denses" :key="n.value" :label="`${n.label}`" :value="n.value"/>
                </VRadioGroup>
              </VSheet>
            </template>
            <template v-if="activeId === 'gui' || activeId === 'dateformat'">
              <VSubheader>{{ $vuetify.lang.t('$vuetify.formattypes.selection') }}</VSubheader>
              <VDivider/>
              <VSheet class="px-4 mb-4">
                <VRadioGroup v-model="formattype" hide-details>
                  <VRadio v-for="n in formattypes" :key="n.value" :label="`${n.label}`" :value="n.value" :hint="n.hint"/>
                </VRadioGroup>
              </VSheet>
            </template>
            <template v-if="activeId === 'mailing' || activeId === 'copies'">
              <VSheet class="px-4 mb-4">
                <VSubheader>{{ $vuetify.lang.t('$vuetify.options.copies') }}</VSubheader>
                <VDivider/>
                <VTreeview
                    v-model="copyTreeItemsSelection"
                    :items="copyTreeItems"
                    :dense="dense"
                    selection-type="leaf"
                    selectable
                    open-all
                />
                <VDivider class="my-4"/>
                <VTextField v-model="emailcc" v-bind="bindingsTextCC" @change="addEmailCC"/>
                <VRow align="center" justify="start" class="mx-0 mb-2">
                  <VCol v-for="(sel, i) in emCC" :key="i" class="shrink pa-1">
                    <VChip close small @click:close="deleteEmailCC(sel.text)">
                      <VIcon small left v-text="sel.icon"/>
                      {{sel.text}}
                    </VChip>
                  </VCol>
                </VRow>
                <VDivider class="my-4"/>
                <VTextField v-model="emailbcc" v-bind="bindingsTextBCC" @change="addEmailBCC"/>
                <VRow align="center" justify="start" class="mx-0 mb-2">
                  <VCol v-for="(sel, i) in emBCC" :key="i" class="shrink pa-1">
                    <VChip close small @click:close="deleteEmailBCC(sel.text)">
                      <VIcon small left v-text="sel.icon"/>
                      {{sel.text}}
                    </VChip>
                  </VCol>
                </VRow>
              </VSheet>
            </template>
          </VSheet>
        </VFlex>
      </VLayout>
    </VSheet>
  </VDialog>
</template>

<style lang="scss" scoped>
  .vx-usersettings__main {
    height: 100%;
      .v-sheet.v-toolbar {
          border-radius: 0;
      }
  }
  .vx-usersettings__layout {
    height: calc(100% - 64px);
    overflow: auto;
    .flex.flex-row {
      display: flex;
    }
  }
  .v-sheet.vx-usersettings__content {
    flex-grow: inherit;
    border: 1px dashed rgba(0, 0, 0, 0.35);
  }
</style>

<style>
  .v-treeview .v-treeview-node__root {
    cursor: pointer;
  }
</style>
